/**
 * ApprovalBanner — human-in-the-loop approve/reject with persistence options.
 *
 * Options:
 *   - Allow once: permit this single tool call
 *   - Allow for session: permit this tool for the rest of the conversation
 *   - Always allow: persist across all conversations (saved to settings)
 *   - Deny: reject this tool call (agent continues without it)
 */

import { useChatStore } from "@/stores/chatStore";
import { Button } from "@/components/common/Button";

export function ApprovalBanner() {
  const { pendingApproval, approveAction, denyAction } = useChatStore();

  if (!pendingApproval) return null;

  return (
    <div className="mx-auto max-w-xl bg-jove-10 border border-jove-60/40 rounded-xl p-4 shadow-sm animate-[scaleIn_200ms_ease-out]">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <div className="text-sm text-space leading-relaxed">
          <p className="font-medium mb-1">Permission Required</p>
          <p className="text-space-50">{pendingApproval.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end flex-wrap">
        <Button variant="ghost" size="sm" onClick={denyAction}>
          Deny
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => approveAction(pendingApproval.action, "once")}
        >
          Allow once
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => approveAction(pendingApproval.action, "session")}
        >
          Allow for session
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => approveAction(pendingApproval.action, "always")}
        >
          Always allow
        </Button>
      </div>
    </div>
  );
}
